import { useState, useRef, useCallback, useEffect } from 'react'
import { useDevToolsStore } from 'shared/store/devtools'

const PASSCODE = '112211'
const CODE_LENGTH = 6

/**
 * Passcode modal presented after the 20-tap gesture when the dev_mode cookie
 * is not present. Enter `112211` to unlock the frontend DevTools.
 */
export function DevPasscodeModal() {
	const visible = useDevToolsStore((s) => s.passcodeModalOpen)
	const [digits, setDigits] = useState('')
	const [error, setError] = useState(false)
	const [shake, setShake] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (visible) {
			setDigits('')
			setError(false)
			setShake(false)
			// Focus the hidden input after the modal opens
			setTimeout(() => inputRef.current?.focus(), 100)
		}
	}, [visible])

	const close = useCallback(() => {
		useDevToolsStore.getState().setPasscodeModalOpen(false)
	}, [])

	const handleChange = useCallback(
		(value: string) => {
			const clean = value.replace(/\D/g, '').slice(0, CODE_LENGTH)
			setDigits(clean)
			setError(false)

			if (clean.length === CODE_LENGTH) {
				if (clean === PASSCODE) {
					const store = useDevToolsStore.getState()
					store.unlock()
					store.setSheetOpen(true)
					store.setPasscodeModalOpen(false)
				} else {
					setError(true)
					setShake(true)
					setTimeout(() => {
						setShake(false)
						setDigits('')
					}, 600)
				}
			}
		},
		[],
	)

	if (!visible) return null

	return (
		<div
			className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
			onClick={close}
		>
			<div
				className={`mx-4 w-full max-w-xs rounded-2xl bg-[var(--tgui--secondary_bg_color,#1c1c1e)] p-6 shadow-2xl ${shake ? 'animate-shake' : ''}`}
				onClick={(e) => e.stopPropagation()}
			>
				<h3 className="mb-1 text-center text-lg font-semibold text-[var(--tgui--text_color,#fff)]">
					Dev Tools
				</h3>
				<p className="mb-5 text-center text-sm text-[var(--tgui--hint_color,#999)]">
					Enter passcode to unlock
				</p>

				{/* Hidden input for keyboard focus */}
				<input
					ref={inputRef}
					type="tel"
					inputMode="numeric"
					autoComplete="off"
					value={digits}
					onChange={(e) => handleChange(e.target.value)}
					className="absolute opacity-0 pointer-events-none"
					aria-label="Passcode input"
				/>

				{/* Dot indicators */}
				<div
					className="flex justify-center gap-3 mb-5"
					onClick={() => inputRef.current?.focus()}
				>
					{Array.from({ length: CODE_LENGTH }, (_, i) => (
						<div
							key={i}
							className={`h-3.5 w-3.5 rounded-full border-2 transition-all duration-200 ${
								i < digits.length
									? error
										? 'border-red-500 bg-red-500'
										: 'border-[var(--tgui--link_color,rgb(41,144,255))] bg-[var(--tgui--link_color,rgb(41,144,255))]'
									: 'border-[var(--tgui--hint_color,#555)] bg-transparent'
							}`}
						/>
					))}
				</div>

				{error && (
					<p className="text-center text-xs text-red-400">
						Incorrect passcode
					</p>
				)}

				<style>{`
					@keyframes shake {
						0%, 100% { transform: translateX(0); }
						20%, 60% { transform: translateX(-8px); }
						40%, 80% { transform: translateX(8px); }
					}
					.animate-shake { animation: shake 0.4s ease-in-out; }
				`}</style>
			</div>
		</div>
	)
}
